import React from "react";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import { Pencil, Upload, X, Plus, Trash2, ArrowLeft } from "lucide-react";
import varifiedIcon from "../../../assests/verifiedIcon.svg";
import "./NavigationalUpdateComponent.css";

const DEFAULT_ICON = "https://cdn-icons-png.flaticon.com/512/1261/1261163.png";

const NavigationalUpdateComponent = ({
  formData,
  setFormData,
  isLocked,
  setIsLocked,
  iconPreview,
  bgPreview,
  useTransparentBg,
  variantIds,
  variantDetails,
  inputValue,
  setInputValue,
  iconInputRef,
  backgroundInputRef,
  isSubmitting,
  isIconUploading,
  isBgUploading,
  isVerifying,
  handleToggleTransparent,
  handleAddId,
  removeId,
  handleClearAllVariants,
  handleBackClick,
  handleSave,
  handleIconFileSelect,
  handleBgFileSelect,
  handleClearIcon,
  handleClearBg,
  iconPath,
  bgPath,
  sourceTab,
  categoryName,
  showSuccessPopup,
  onSuccessClose,
  updatedItemName,
  updatedItemId,
}) => {
  const S3_BASE_URL = "https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/";

  const getVariantImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `${S3_BASE_URL}${imagePath.replace(/^\//, '')}`;
  };

  return (
    <div className="nav-update-container">
      <Sidebar />
      
      <div className="nav-update-content">
        <div className="nav-update-header">
          <button onClick={handleBackClick} className="nav-update-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1>{categoryName || "Update Navigational"}</h1>
        </div>

        {/* Show source tab info if needed */}
        {sourceTab === "variant" && categoryName && (
          <div className="nav-update-category-banner">
            <div className="nav-update-category-banner-content">
              <span className="nav-update-category-label">Editing Category: </span>
              <span className="nav-update-category-name">{categoryName}</span>
            </div>
          </div>
        )}

        <div className="nav-update-scrollable">
          <div className="nav-update-card">
            <div className="nav-update-inner">
              {/* Mapping Type */}
              <div className="nav-update-mapping-indicator">
                <label>Current Mapping Type</label>
                <div className="nav-update-mapping-badge">
                  <div className="nav-update-mapping-dot"></div>
                  Variant
                </div>
                <div>
                  {isLocked && (
                    <button onClick={() => setIsLocked(false)} className="nav-update-edit-btn">
                      <Pencil size={18} /> Edit
                    </button>
                  )}
                </div>
              </div>

              {/* Name Field - No validation */}
              <div className="nav-update-form-field">
                <label>Navigational Name</label>
                <input
                  type="text"
                  readOnly={isLocked}
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`nav-update-form-input ${isLocked ? "readonly" : ""}`}
                  placeholder="Eg. Fresh Food, Electronics and Accessories"
                />
              </div>

              {/* Description Field - Optional */}
              <div className="nav-update-form-field">
                <label>Description (Optional)</label>
                <input
                  type="text"
                  readOnly={isLocked}
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`nav-update-form-input ${isLocked ? "readonly" : ""}`}
                  placeholder="Eg. Details of the product"
                />
              </div>

              {/* Icon Upload - No validation */}
              <div className="nav-update-form-field">
                <label>Icon</label>
                <div className="nav-update-upload-wrapper">
                  <div className="nav-update-upload-container">
                    <input
                      type="file"
                      ref={iconInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleIconFileSelect}
                      disabled={isLocked}
                    />
                    <div 
                      className={`nav-update-upload-area ${isLocked ? "disabled" : ""}`} 
                      onClick={() => !isLocked && iconInputRef.current?.click()}
                    >
                      {iconPreview ? (
                        <div className="nav-update-preview-wrapper">
                          <img src={iconPreview || DEFAULT_ICON} alt="Icon" className="nav-update-preview-icon" />
                          {!isLocked && (
                            <button 
                              className="nav-update-clear-btn" 
                              onClick={(e) => { e.stopPropagation(); handleClearIcon(); }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="nav-update-upload-content">
                          <Upload size={32} className="nav-update-upload-icon" />
                          <p>Choose Icon Image</p>
                          <small>Click to select</small>
                        </div>
                      )}
                    </div>
                  </div>
                  {isIconUploading && (
                    <div className="nav-update-uploading">
                      <div className="spinner-small"></div>
                      <span>Uploading...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Background Section - No validation */}
              <div className="nav-update-form-field">
                <label>Background Image</label>
                
                {/* Checkbox - Always visible, disabled in locked mode */}
                <label className="nav-update-checkbox">
                  <input 
                    type="checkbox" 
                    checked={useTransparentBg} 
                    onChange={handleToggleTransparent}
                    disabled={isLocked}
                  />
                  <span>Use Transparent Background</span>
                </label>

                {/* Show upload section only when NOT using transparent background */}
                {!useTransparentBg && (
                  <div className="nav-update-upload-wrapper">
                    <div className="nav-update-upload-container">
                      <input
                        type="file"
                        ref={backgroundInputRef}
                        hidden
                        accept="image/*"
                        onChange={handleBgFileSelect}
                        disabled={isLocked}
                      />
                      <div 
                        className={`nav-update-upload-area ${isLocked ? "disabled" : ""}`} 
                        onClick={() => !isLocked && backgroundInputRef.current?.click()}
                      >
                        {bgPreview ? (
                          <div className="nav-update-preview-wrapper-bg">
                            <img src={bgPreview} alt="Background" className="nav-update-preview-bg" />
                            {!isLocked && (
                              <button 
                                className="nav-update-clear-btn" 
                                onClick={(e) => { e.stopPropagation(); handleClearBg(); }}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="nav-update-upload-content">
                            <Upload size={32} className="nav-update-upload-icon" />
                            <p>Choose Background Image</p>
                            <small>Click to select</small>
                          </div>
                        )}
                      </div>
                    </div>
                    {isBgUploading && (
                      <div className="nav-update-uploading">
                        <div className="spinner-small"></div>
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Variant IDs - Modern Preview Box */}
              <div className="nav-update-variant-section">
                <div className="nav-update-variant-header">
                  <label>Add Variant IDs</label>
                  <span className="nav-update-variant-count">{variantIds.length} Variant IDs added</span>
                </div>

                {/* Add Variant Input */}
                {!isLocked && (
                  <div className="nav-update-add-variant">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddId()}
                      placeholder="Enter IDs separated by space or comma"
                      disabled={isVerifying}
                    />
                    <button onClick={handleAddId} disabled={isVerifying || !inputValue.trim()}>
                      {isVerifying ? "Verifying..." : "Add"}
                    </button>
                  </div>
                )}
                
                {variantIds.length > 0 && (
                  <div className="nav-update-variant-preview-box">
                    <div className="nav-update-preview-header">
                      <div className="nav-update-preview-title-group">
                        <div className="nav-update-live-dot"></div>
                        <span className="nav-update-preview-title">VARIANTS PREVIEW</span>
                      </div>
                      <span className="nav-update-preview-count">{variantIds.length} ITEMS</span>
                    </div>
                    <div className="nav-update-preview-grid">
                      {variantIds.map((id) => {
                        const details = variantDetails[id];
                        return (
                          <div key={id} className="nav-update-preview-item">
                            <div className="nav-update-item-thumb">
                              {details?.image ? (
                                <img 
                                  src={getVariantImageUrl(details.image)} 
                                  alt={details.name || `Variant ${id}`}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="nav-update-placeholder-img"></div>
                              )}
                            </div>
                            <div className="nav-update-item-info">
                              <span className="nav-update-item-name">{details?.name || `Variant Product ${id}`}</span>
                              <span className="nav-update-item-id">ID: {id}</span>
                            </div>
                            {!isLocked && (
                              <button 
                                className="nav-update-item-remove"
                                onClick={() => removeId(id)}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Clear All Button */}
                {!isLocked && variantIds.length > 0 && (
                  <div className="nav-update-clear-variants">
                    <button onClick={handleClearAllVariants}>
                      <Trash2 size={14} /> Clear All Variants
                    </button>
                  </div>
                )}
              </div>

              {/* Status Buttons */}
              <div className="nav-update-status-section">
                <div className="nav-update-status-buttons">
                  <button 
                    onClick={() => setFormData({ ...formData, active: true })} 
                    className={`nav-update-status-btn ${formData.active === true ? "active" : ""}`} 
                    disabled={isLocked}
                  >
                    ACTIVE
                  </button>
                  <button 
                    onClick={() => setFormData({ ...formData, active: false })} 
                    className={`nav-update-status-btn ${formData.active === false ? "inactive" : ""}`} 
                    disabled={isLocked}
                  >
                    INACTIVE
                  </button>
                </div>
              </div>

              {/* Save Button - Always enabled */}
              {!isLocked && (
                <div className="nav-update-save-section">
                  <button 
                    disabled={isSubmitting} 
                    onClick={handleSave} 
                    className="nav-update-save-btn"
                  >
                    {isSubmitting ? "Updating..." : "Update Navigational"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="nav-update-success-overlay">
          <div className="nav-update-success-modal">
            <div className="nav-update-success-icon">
              <img style={{width:"100%", height:"100%"}} src={varifiedIcon} alt="success" />
            </div>
            <h3 className="nav-update-success-title">Updated Successfully!</h3>
            <p className="nav-update-success-message">Navigational has been updated successfully.</p>
            
            <div className="nav-update-success-details">
              <div className="nav-update-success-detail-row">
                <span className="nav-update-success-label">Name:</span>
                <span className="nav-update-success-value">{updatedItemName}</span>
              </div>
              <div className="nav-update-success-detail-row">
                <span className="nav-update-success-label">ID:</span>
                <span className="nav-update-success-value">{updatedItemId}</span>
              </div>
            </div>
            
            <button 
              className="nav-update-success-btn" 
              onClick={onSuccessClose}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationalUpdateComponent;